import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { buildApiUrl } from '../config/api';
import { schooldetails } from '../redux/schooldetails';

/**
 * Ghana new schools only: persistent reminder until GHS 400 onboarding (registration, onboarding, training) is paid via Paystack.
 * Existing schools do not have onboardingFeePolicyApplies set.
 */
export default function OnboardingFeeBanner() {
    const dispatch = useDispatch();
    const admin = useSelector((state) => state.admin) || {};
    const schooldetails_ = useSelector((state) => state.schooldetails) || [];
    const school = schooldetails_[0];
    const [paying, setPaying] = React.useState(false);
    const [snack, setSnack] = React.useState(null);
    const returnHandled = React.useRef(false);

    const show = school && school.onboardingFeePolicyApplies === true && school.onboardingFeePaid !== true;

    React.useEffect(() => {
        if (!show || returnHandled.current) return;
        const q = new URLSearchParams(window.location.search);
        if (q.get('sis_onboarding') !== '1') return;
        const reference = q.get('reference') || q.get('trxref');
        if (!reference) return;
        returnHandled.current = true;
        axios
            .post(buildApiUrl('/api/schools/onboarding-verify'), { reference })
            .then((res) => {
                if (res.data && res.data.school) {
                    dispatch(schooldetails({ data: [res.data.school] }));
                }
                const path = window.location.pathname || '/';
                const hash = window.location.hash || '#/';
                window.history.replaceState({}, document.title, path + hash);
                setSnack('Payment recorded. Thank you.');
            })
            .catch((err) => {
                returnHandled.current = false;
                setSnack(err.response?.data?.message || err.message || 'Could not verify payment');
            });
    }, [show, dispatch]);

    React.useEffect(() => {
        if (!snack) return undefined;
        const t = setTimeout(() => setSnack(null), 6000);
        return () => clearTimeout(t);
    }, [snack]);

    const handlePay = async () => {
        if (!admin.schoolID) {
            setSnack('Missing school session');
            return;
        }
        setPaying(true);
        setSnack(null);
        const callbackUrl = `${window.location.origin}${window.location.pathname || '/'}?sis_onboarding=1`;
        try {
            const res = await axios.post(buildApiUrl('/api/schools/onboarding-initialize'), {
                schoolID: admin.schoolID,
                callbackUrl
            });
            if (res.data && res.data.authorization_url) {
                window.location.href = res.data.authorization_url;
                return;
            }
            setSnack('Could not start payment');
        } catch (err) {
            setSnack(err.response?.data?.message || err.message || 'Payment start failed');
        } finally {
            setPaying(false);
        }
    };

    if (!show) {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    p: 1.5,
                    pt: 0,
                    pointerEvents: 'none',
                    '& > *': { pointerEvents: 'auto' }
                }}
            >
                <Alert
                    severity="warning"
                    variant="filled"
                    sx={{
                        alignItems: 'center',
                        boxShadow: 6,
                        maxWidth: 960,
                        mx: 'auto'
                    }}
                    action={
                        <Button color="inherit" variant="outlined" disabled={paying} onClick={handlePay} sx={{ borderColor: 'rgba(255,255,255,0.8)' }}>
                            {paying ? 'Please wait…' : 'Pay GHS 400'}
                        </Button>
                    }
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Complete registration: onboarding & training package
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Pay <strong>GHS 400</strong> (registration, onboarding, and training) via Paystack. After payment, your school can use the platform for the
                        first term without the standard subscription charge. This notice stays until payment is completed.
                    </Typography>
                </Alert>
            </Box>
            {snack && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 100,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1400,
                        bgcolor: 'background.paper',
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        boxShadow: 3,
                        fontSize: 14
                    }}
                >
                    {snack}
                </Box>
            )}
        </>
    );
}
